/**
 * Translation event channel symbol
 *
 * Used to avoid name collision when attaching channel to global object
 *
 * @constant
 * @type {Symbol}
 *
 * @access private
 */
var CHANNEL = Symbol("Translation event channel");
/**
 * Loader communication channel
 *
 * Designed for client-side. Queue keys which were preloaded before loader is
 * created. Once loader subscribes to channel, channel acts as proxy and
 * forwards all requested keys to loader directly.
 *
 * Based on {@link Set} to deduplicate keys.
 *
 * @access private
 */

class LoaderChannel extends Set {
  /**
   * @access private
   */
  constructor() {
    super();
    /**
     * Function to proxy all key requests to
     *
     * @type {?Function}
     * @access private
     */

    this.listener = null;
  }
  /**
   * Queue keys if loader is not available; request to load them otherwise.
   *
   * @access private
   *
   * @param {Array.<string>} aliases
   */


  add(aliases) {
    if (this.listener) {
      this.listener(aliases);
      return this;
    } // Loop as set allows to add only one value at a time


    for (var alias of aliases) {
      super.add(alias);
    }
  }
  /**
   * Retrieve/create channel instance and queue/load keys
   *
   * @access private
   *
   * @param {Array.<string>} aliases
   */


  static enqueue(aliases) {
    var channel = LoaderChannel.getChannel();
    channel.add(aliases);
  }
  /**
   * Retrieve/create channel instance and attach loader
   *
   * @access private
   *
   * @param {Loader} listener
   */


  static subscribe(listener) {
    var channel = LoaderChannel.getChannel();
    channel.listener = listener; // Convert set to array

    listener(Array.from(channel));
  }
  /**
   * Retrieve channel instance or create one on `window` if it doesn’t exist
   *
   * @access private
   *
   * @returns {LoaderChannel}
   */


  static getChannel() {
    return window[CHANNEL] = window[CHANNEL] || new LoaderChannel();
  }
  /**
   * Remove existing channel instance
   *
   * WARNING: This method is a utility for testing clean up and is not supposed
   * to be used directly. If you come across use case for it, consult with
   * project owners.
   *
   * @access private
   */


  static destroyChannel() {
    if (window[CHANNEL]) {
      delete window[CHANNEL];
    }
  }

}

export { LoaderChannel };
